import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Paperclip, Monitor, CheckCircle2, X, Maximize2, Minimize2, AtSign, LayoutList, Lightbulb } from 'lucide-react';
import { TaskCommentMention, TaskCommentImage, TaskCommentVideo, TaskCommentFileAttachment } from './taskCommentNodes.js';
import { createTaskCommentSuggestion } from './taskCommentSuggestion.js';
import { uploadAndInsertFile, insertVideoEmbed } from '../../lib/taskCommentUpload.js';
import { useScreenRecordStore } from '../../../../shared/media/useScreenRecordStore.js';
import LilMenu from '../../../docs/components/document-text/lilmenu/lilmenu.jsx';
import './TaskCommentEditor.css';

const SMART_TAG_TERMS = {
    urgente: 'priorityHigh', bug: 'bugReport', revisar: 'reviewNeeded',
    reunión: 'meeting', reunirnos: 'meeting', juntarnos: 'meeting', vernos: 'meeting', agendar: 'meeting', llamada: 'meeting', cita: 'meeting', meet: 'meeting', meeting: 'meeting', sync: 'meeting',
    recordatorio: 'reminder', recordar: 'reminder', recuérdame: 'reminder', alert: 'reminder', alarma: 'reminder', avisar: 'reminder', ping: 'reminder',
    documentación: 'documentation', docs: 'documentation', doc: 'documentation', manual: 'documentation', guía: 'documentation', referencia: 'documentation',
    presentación: 'presentations', deck: 'presentations', slide: 'presentations', slides: 'presentations', demo: 'presentations', propuesta: 'presentations',
    encuesta: 'survey', votación: 'survey', votar: 'survey', opinión: 'survey', feedback: 'survey',
    flujo: 'flow', workflow: 'flow', proceso: 'flow', pasos: 'flow', diagrama: 'flow',
};

function detectSmartTags(text, translate) {
    const lower = text.toLowerCase();
    const found = new Set();
    Object.entries(SMART_TAG_TERMS).forEach(([term, key]) => {
        if (lower.includes(term)) found.add(translate(`smartTags.${key}`));
    });
    return Array.from(found);
}

// Reemplaza el `contentEditable` manual de creación (ComponentTaskHoy.jsx:5851-5859) y el
// `<textarea>` roto de edición (líneas 5557-5564) por un único editor TipTap, reutilizado para
// ambos flujos -- ver .claude/plans/editor-comentarios-tareas.md. `resetKey` dispara la carga
// de `content` (cambia el `comment_id` en edición, o un contador que se incrementa después de
// cada envío en creación) -- no se pasa `content` como prop "controlada" en cada tecleo, eso
// movería el cursor.
export function TaskCommentEditor({
    content = '',
    mode = 'create',
    resetKey,
    onSubmit,
    onSave,
    onCancel,
    users = [],
    userId,
    placeholder,
    autoFocus = false,
    translate,
    onContentChange,
    onMentionSelect,
    onSmartTagsChange,
    onEnterTaskMode,
    onEnterSuggestionMode,
    expandable = false,
    onExpand,
    isExpanded = false,
    onCollapse,
    className = '',
}) {
    const usersRef = useRef(users);
    useEffect(() => { usersRef.current = users; }, [users]);

    const mentionActiveRef = useRef(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDraggingFile, setIsDraggingFile] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    
    const [lilMenu, setLilMenu] = useState({ isOpen: false, position: { top: 0, left: 0 } });
    const [linkContext, setLinkContext] = useState(null);

    // `useEditor()` abajo se crea UNA sola vez (deps vacías -- recrearlo en cada cambio de
    // props perdería cursor/historial de undo). Sus `editorProps` (handleKeyDown/handleDrop/
    // onUpdate) quedan fijados a los valores del primer render si cierran sobre props directo
    // -- `latestRef` es la indirección estándar para que esos callbacks, ya fijos, sigan
    // viendo props/handlers frescos en cada evento futuro. Se asigna al final del cuerpo del
    // componente (después de definir handleSubmit/handleUpload), antes del `return`.
    const latestRef = useRef({});

    const getUsers = useCallback(() => usersRef.current, []);
    const setMentionActive = useCallback((active) => { mentionActiveRef.current = active; }, []);
    // Integración main→dev, epic D1.4 — reenvía la mención elegida al contenedor (chequeo de
    // acceso al documento estructurado). Lee `latestRef` para no recrear el editor.
    const handleMentionSelect = useCallback((props) => { latestRef.current.onMentionSelect?.(props); }, []);

    const extensions = useMemo(() => [
        StarterKit.configure({
            heading: { levels: [1, 2, 3] },
            link: false, // se agrega aparte, con target="_blank" por default
        }),
        Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
        TaskList,
        TaskItem.configure({ nested: false }),
        Placeholder.configure({ placeholder: () => placeholder || '' }),
        TaskCommentMention.configure({
            // `getUsers`/`setMentionActive` no leen/mutan el ref durante el render -- son
            // funciones que TipTap invoca después, dentro de su plugin de sugerencias (al
            // escribir "@" o al cerrar el popup). El linter no puede probar eso estáticamente
            // (mismo patrón que cualquier configuración de una librería externa con un ref
            // como escape hatch para no crear el editor de nuevo en cada cambio de props).
            // eslint-disable-next-line react-hooks/refs
            suggestion: createTaskCommentSuggestion({
                getUsers,
                onActiveChange: setMentionActive,
                onMentionSelect: handleMentionSelect,
            }),
        }),
        TaskCommentImage,
        TaskCommentVideo,
        TaskCommentFileAttachment,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], []);

    const editor = useEditor({
        extensions,
        content,
        autofocus: autoFocus,
        immediatelyRender: false,
        editorProps: {
            attributes: { class: 'task-comment-rich-editor', 'aria-label': placeholder || '' },
            handleKeyDown: (_view, event) => {
                if (latestRef.current.mode === 'create' && event.key === 'Enter' && !event.shiftKey && !mentionActiveRef.current) {
                    event.preventDefault();
                    latestRef.current.handleSubmit();
                    return true;
                }
                return false;
            },
            handleDrop: (_view, event) => {
                const file = event.dataTransfer?.files?.[0];
                if (!file) return false;
                event.preventDefault();
                latestRef.current.handleUpload(file);
                return true;
            },
            // Sin esto, pegar una captura de pantalla (Cmd+V, el flujo más común para adjuntar
            // una imagen) caía en el parseo de portapapeles por default de TipTap en vez de
            // subir el archivo -- el `<img>` quedaba insertado sin `src` (bug real reportado en
            // producción, comentario `c67382ec-...` de la tarea "pgbouncer para el cluster de
            // k8s"). Mismo patrón que ya tenía `handleDrop` arriba, y que `CommentArea.jsx`
            // (comentarios de docs/dataindex) ya usaba para pegar imágenes.
            handlePaste: (_view, event) => {
                const file = event.clipboardData?.files?.[0];
                if (!file) return false;
                event.preventDefault();
                latestRef.current.handleUpload(file);
                return true;
            },
        },
        onUpdate: ({ editor: ed }) => {
            const cb = latestRef.current.onSmartTagsChange;
            if (cb) cb(detectSmartTags(ed.getText(), latestRef.current.translate));
            latestRef.current.onContentChange?.(ed.getHTML());
        },
        onSelectionUpdate: ({ editor: ed }) => {
            const { state, view } = ed;
            const { selection } = state;
            if (selection.empty) {
                setLilMenu(prev => prev.isOpen ? { ...prev, isOpen: false } : prev);
                setLinkContext(null);
                return;
            }

            const { from, to } = selection;
            const start = view.coordsAtPos(from);
            const end = view.coordsAtPos(to);
            const top = Math.max(start.top, end.top) + window.scrollY + 20; 
            const left = start.left + window.scrollX;

            setLilMenu({
                isOpen: true,
                position: { top, left }
            });
        }
    }, []);

    // Carga el contenido inicial (o el de un comentario distinto) sin recrear el editor.
    // `content` a propósito NO está en las deps -- solo dispara la carga cuando cambia
    // `resetKey` (el `comment_id` en edición, un contador en creación), no en cada render del
    // padre; incluir `content` movería el cursor/perdería lo que el usuario esté escribiendo.
    useEffect(() => {
        if (!editor) return;
        editor.commands.setContent(content || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, resetKey]);

    // Integración main→dev, epic D1.4 — al entrar en modo sugerencia desde la bitácora el
    // editor debe recibir el foco aunque ya estuviera montado.
    useEffect(() => {
        if (!autoFocus || !editor) return undefined;
        const timer = setTimeout(() => editor.commands.focus('end'), 60);
        return () => clearTimeout(timer);
    }, [editor, autoFocus, resetKey]);

    useEffect(() => () => editor?.destroy(), [editor]);

    const handleUpload = async (file) => {
        if (!editor) return;
        await uploadAndInsertFile(editor, file, { userId, onProgress: setUploadProgress });
    };

    // Grabador global (useScreenRecordStore): al presionar el botón guardamos el `finishedTick`
    // actual; cuando el store sube el tick (una grabación terminó de subir) y fuimos NOSOTROS
    // quienes pusimos `isRecording` en true, insertamos el video. Varias instancias de este
    // editor pueden estar montadas a la vez (inline y modal) — cada una guarda su propio tick.
    const recStartTickRef = useRef(0);
    const finishedTick = useScreenRecordStore((s) => s.finishedTick);
    useEffect(() => {
        if (!isRecording || finishedTick <= recStartTickRef.current) return;
        setIsRecording(false);
        const url = useScreenRecordStore.getState().lastUrl;
        if (url) insertVideoEmbed(editor, url);
    }, [editor, isRecording, finishedTick]);

    const handleSubmit = () => {
        if (!editor || editor.isEmpty) return;
        const html = editor.getHTML();
        onSubmit?.(html);
        editor.commands.clearContent();
        onSmartTagsChange?.([]);
    };

    const handleSave = () => {
        if (!editor) return;
        onSave?.(editor.getHTML());
    };

    const onLilMenuSelect = (action, value) => {
        if (!editor) return;
        editor.commands.focus();

        if (action === 'format') {
            if (value === 'bold') editor.chain().focus().toggleBold().run();
            if (value === 'italic') editor.chain().focus().toggleItalic().run();
            if (value === 'strikeThrough') editor.chain().focus().toggleStrike().run();
            if (value === 'removeFormat') editor.chain().focus().unsetAllMarks().run();
        }
        if (action === 'heading') {
            editor.chain().focus().toggleHeading({ level: value }).run();
        }
        if (action === 'text') {
            editor.chain().focus().setParagraph().run();
        }
        if (action === 'list' && value === 'checklist') {
            editor.chain().focus().toggleTaskList().run();
        }
        if (action === 'list' && value === 'bullet') {
            editor.chain().focus().toggleBulletList().run();
        }
        if (action === 'insert-block' && value === 'code') {
            editor.chain().focus().toggleCodeBlock().run();
        }
        if (action === 'link') {
            editor.chain().focus().setLink({ href: value.url, target: '_blank' }).run();
        }
        // 04-enforce.md 4.3 -- `LilMenu`'s "Convertir a Tarea/Documento" (shared with the docs
        // editor, donde sí tiene handler) nunca se cableó del lado de los comentarios de tarea:
        // el `action === 'convert'` disparaba un `convert-to-task`/`convert-to-doc` window
        // CustomEvent que ningún listener escuchaba. Sin consumidor => canal huérfano, retirado.
        // Si se quiere la conversión acá, necesita implementación real (crear la tarea/doc), no
        // reponer el `dispatchEvent`.
        setLilMenu(prev => ({ ...prev, isOpen: false }));
    };

    useEffect(() => {
        latestRef.current = { mode, onSubmit, onSmartTagsChange, onContentChange, onMentionSelect, translate, handleSubmit, handleUpload };
    });

    return (
        <div
            className={`task-comment-editor-shell ${className} ${isDraggingFile ? 'dragging' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDraggingFile(false); }}
            onDrop={(e) => {
                e.preventDefault();
                setIsDraggingFile(false);
                const file = e.dataTransfer?.files?.[0];
                if (file) handleUpload(file);
            }}
        >
            {/* `EditorContent` mete un <div> propio sin clase ENTRE este wrapper y el
                `.task-comment-rich-editor` real (el div contenteditable de ProseMirror) --
                sin `className` acá, ese div intermedio no es flex, así que un `flex:1` en
                `.task-comment-rich-editor` no hace nada (las propiedades flex solo aplican
                si el padre inmediato es `display:flex`). Ver TaskCommentEditorModal.css. */}
            <EditorContent editor={editor} className="task-comment-editor-content-wrapper" />

            {uploadProgress > 0 && (
                <div className="comment-upload-progress-container">
                    <div className="comment-upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
                </div>
            )}

            <div className="task-comment-editor-actions">
                <div className="task-comment-editor-actions-left">
                    <label className="toolbar-btn clip-btn" title={translate('comments.attachFile')} aria-label={translate('comments.attachFile')}>
                        <Paperclip size={14} />
                        <input
                            type="file"
                            aria-label={translate('comments.attachFile')}
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (file) await handleUpload(file);
                                e.target.value = '';
                            }}
                        />
                    </label>
                    <button
                        type="button"
                        className="toolbar-btn mention-btn"
                        title={translate('comments.mentionUser') || 'Mention'}
                        aria-label={translate('comments.mentionUser') || 'Mention'}
                        onClick={() => {
                            if (editor) {
                                editor.chain().focus().insertContent('@').run();
                            }
                        }}
                    >
                        <AtSign size={14} />
                    </button>
                    <button
                        type="button"
                        className={`toolbar-btn monitor-btn ${isRecording ? 'recording' : ''}`}
                        title={translate('comments.recordScreen')}
                        aria-label={translate('comments.recordScreen')}
                        onClick={() => {
                            recStartTickRef.current = useScreenRecordStore.getState().finishedTick;
                            setIsRecording(true);
                            useScreenRecordStore.getState().toggle();
                        }}
                    >
                        <Monitor size={14} />
                    </button>
                    {onEnterSuggestionMode && !isExpanded && (
                        <button type="button" className="toolbar-btn suggestion-mode-btn" title="Modo Sugerencia / Mejora" aria-label="Modo Sugerencia / Mejora" onClick={onEnterSuggestionMode}>
                            <Lightbulb size={13} color="#c084fc" />
                        </button>
                    )}
                    {onEnterTaskMode && !isExpanded && (
                        <button type="button" className="toolbar-btn task-mode-btn" title="Modo Tarea" aria-label="Modo Tarea" onClick={onEnterTaskMode}>
                            <LayoutList size={13} color="#3b82f6" />
                        </button>
                    )}
                    {expandable && !isExpanded && (
                        <button type="button" className="toolbar-btn expand-btn" title={translate('comments.expandEditor')} aria-label={translate('comments.expandEditor')} onClick={onExpand}>
                            <Maximize2 size={13} />
                        </button>
                    )}
                    {isExpanded && (
                        <button type="button" className="toolbar-btn expand-btn" title={translate('comments.collapseEditor')} aria-label={translate('comments.collapseEditor')} onClick={onCollapse}>
                            <Minimize2 size={13} />
                        </button>
                    )}
                </div>

                <div className="task-comment-editor-actions-right">
                    {mode === 'create' ? (
                        <button type="button" className="comment-submit-btn" onClick={handleSubmit}>
                            {translate('comments.send')}
                        </button>
                    ) : (
                        <>
                            <button type="button" className="action-btn save" onClick={handleSave} aria-label={translate('comments.saveAria')} title={translate('comments.saveAria')}>
                                <CheckCircle2 size={14} />
                            </button>
                            <button type="button" className="action-btn cancel" onClick={onCancel} aria-label={translate('comments.cancelEditAria')} title={translate('comments.cancelEditAria')}>
                                <X size={14} />
                            </button>
                        </>
                    )}
                </div>
            </div>
            {lilMenu.isOpen && createPortal(
                <LilMenu
                    isOpen={lilMenu.isOpen}
                    position={lilMenu.position}
                    onSelect={onLilMenuSelect}
                    onClose={() => { setLilMenu(prev => ({ ...prev, isOpen: false })); setLinkContext(null); }}
                    linkContext={linkContext}
                    mode="simple"
                />,
                document.body
            )}
        </div>
    );
}
